/** Associates a string type with a constant key used for `localStorage` */
class LocalStorageKey<T extends string> {
    key: string;
    isValid?: (x: string) => x is T;

    /**
     * Constructs a new `LocalStorageKey`
     *
     * @param key the constant key for `localStorage` to associate `T` with
     * @param isValid an optional closure to check a stored value for validity (see `get`).
     */
    constructor(key: string, isValid?: (x: string) => x is T) {
        this.key = key;
        this.isValid = isValid;
    }

    /**
     * Retrieves the key from the local storage
     *
     * If `isValid` is set and returns `false` then this method will return `null`.
     *
     * @returns the (valid) value associated with the key
     */
    get() {
        const value = window.localStorage.getItem(this.key);
        if (value === null) return null;
        else if (this.isValid) return this.isValid(value) ? value : null;
        else return value as T;
    }

    /**
     * Sets the key's value in the local storage
     *
     * @param value teh value to set
     */
    set(value: T) {
        window.localStorage.setItem(this.key, value);
    }

    /** Removes the key from the local storage */
    remove() {
        window.localStorage.removeItem(this.key);
    }
}

/**
 * An available login method
 *
 * `"Oidc"` is deprecated and should be interpreted as `"Oidc-{uuid}"` (if possible).
 */
type LoginMethod = "Oidc" | "External" | `Oidc-${string}`;

/**
 * The user's preferred login method.
 *
 * This method will be selected automatically if available instead of asking the user.
 * It is not really the "preferred". It's more like the "last successful".
 */
export const PREFERRED_LOGIN = new LocalStorageKey<LoginMethod>("preferred-tailscale-login");

/**
 * The login method the user is currently attempting.
 *
 * Will be written to `PREFERRED_LOGIN` after a successful login.
 */
export const PENDING_LOGIN = new LocalStorageKey<LoginMethod>("pending-tailscale-login");
